import React, { useState, useEffect, useCallback } from 'react';
import { Search, X } from 'lucide-react';

interface BlogSearchProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
}

const BlogSearch: React.FC<BlogSearchProps> = ({
  value,
  onChange,
  placeholder = "Search articles...",
  debounceMs = 300
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);

  // Debounce the onChange callback
  useEffect(() => {
    const timer = setTimeout(() => {
      onChange(localValue);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localValue, onChange, debounceMs]);

  // Sync local value with external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleClear = useCallback(() => {
    setLocalValue('');
    onChange('');
  }, [onChange]);

  return (
    <div className="relative w-full max-w-md">
      <div
        className={`
          relative flex items-center rounded-xl border bg-sidebar
          transition-all duration-300
          ${isFocused
            ? 'border-primary shadow-lg shadow-primary/10 ring-4 ring-primary/5'
            : 'border-border hover:border-primary/50'
          }
        `}
      >
        {/* Search icon */}
        <div className="absolute left-4 pointer-events-none">
          <Search
            className={`
              w-5 h-5 transition-colors duration-300
              ${isFocused ? 'text-primary' : 'text-muted-foreground'}
            `}
          />
        </div>

        {/* Input */}
        <input
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          className="
            w-full py-3 pl-12 pr-10 bg-transparent
            text-foreground placeholder-muted-foreground
            text-sm font-medium
            focus:outline-none
            rounded-xl
          "
        />

        {/* Clear button */}
        {localValue && (
          <button
            onClick={handleClear}
            className="absolute right-3 p-1 rounded-full text-muted-foreground hover:text-foreground hover:bg-background transition-all duration-200"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search hint */}
      {isFocused && !localValue && (
        <div className="absolute top-full left-0 right-0 mt-2 p-3 rounded-lg bg-sidebar border border-border shadow-lg z-10">
          <p className="text-xs text-muted-foreground">
            Search by title, excerpt, or tags
          </p>
        </div>
      )}
    </div>
  );
};

export default BlogSearch;
